import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  formatSplitMoney,
  getOrderItemRemainder,
  getOrderItemUnitLabel,
} from "../../../shared/orderItemPriceSplits";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import OrderItemPriceSplitModal from "./OrderItemPriceSplitModal";
import type {
  EmptyModalState,
  OrderItemForPriceSplit,
  OrderItemPriceSplit,
  PriceSplitMutationResult,
  PriceSplitPayload,
} from "../../../types/orders";

type SplitTableRow = (OrderItemPriceSplit | RemainderRow) & {
  rowType: "split" | "remainder";
};

interface RemainderRow {
  id: string;
  quantity: number;
  unit_cost: number;
  subtotal_amount: number;
  tax_amount: number;
  total_cost: number;
}

interface OrderItemPriceSplitsTableProps {
  orderItem: OrderItemForPriceSplit;
  setEmptyModelOpen: (state: EmptyModalState) => void;
  onCreatePriceSplit: (
    orderItemId: number,
    payload: PriceSplitPayload,
  ) => Promise<PriceSplitMutationResult>;
  onUpdatePriceSplit: (
    orderItemId: number,
    splitId: number,
    payload: PriceSplitPayload,
  ) => Promise<PriceSplitMutationResult>;
  onDeletePriceSplit: (
    orderItemId: number,
    splitId: number,
  ) => Promise<PriceSplitMutationResult>;
  canEditSplits: boolean;
}

export default function OrderItemPriceSplitsTable(
  props: OrderItemPriceSplitsTableProps,
) {
  const {
    orderItem,
    setEmptyModelOpen,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    canEditSplits,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const splits = orderItem?.price_splits || [];
  const remainder = getOrderItemRemainder(orderItem);
  const unitLabel = getOrderItemUnitLabel(orderItem?.unit_type);
  const splitCount = splits.length;

  const tableRows: SplitTableRow[] = [
    ...splits.map((split) => ({ ...split, rowType: "split" as const })),
    ...(remainder.quantity > 0
      ? [
          {
            id: `remainder-${orderItem.id}`,
            rowType: "remainder" as const,
            quantity: remainder.quantity,
            unit_cost: remainder.unit_cost,
            subtotal_amount: remainder.subtotal_amount,
            tax_amount: remainder.tax_amount,
            total_cost: remainder.total_cost,
          },
        ]
      : []),
  ];

  const openCreateModal = () => {
    setEmptyModelOpen({
      childComponent: (
        <OrderItemPriceSplitModal
          orderItem={orderItem}
          setOpen={setEmptyModelOpen}
          onConfirm={async (payload) => {
            const result = await onCreatePriceSplit(orderItem.id, payload);
            if (result?.success) {
              setEmptyModelOpen({ childComponent: null, open: false });
              setIsExpanded(true);
            }
          }}
        />
      ),
      open: true,
    });
  };

  const openEditModal = (split: OrderItemPriceSplit) => {
    setEmptyModelOpen({
      childComponent: (
        <OrderItemPriceSplitModal
          orderItem={orderItem}
          split={split}
          setOpen={setEmptyModelOpen}
          onConfirm={async (payload) => {
            const result = await onUpdatePriceSplit(
              orderItem.id,
              split.id,
              payload,
            );
            if (result?.success) {
              setEmptyModelOpen({ childComponent: null, open: false });
            }
          }}
        />
      ),
      open: true,
    });
  };

  const askDeleteSplit = (split: OrderItemPriceSplit) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={async () => {
            const result = await onDeletePriceSplit(orderItem.id, split.id);
            if (result?.success) {
              setEmptyModelOpen({ childComponent: null, open: false });
            }
          }}
          message={`Delete this ${split.quantity} ${unitLabel} price split at ${formatSplitMoney(split.unit_cost)}?`}
          hideReason={true}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const quantityBody = (rowData: SplitTableRow) => (
    <p>
      {rowData.quantity} {unitLabel}
      {rowData.rowType === "remainder" ? (
        <span className="ml-2 text-xs text-gray-500">(remainder)</span>
      ) : null}
    </p>
  );

  const moneyBody = (value: string | number | null | undefined) => (
    <p>{formatSplitMoney(value)}</p>
  );

  const actionsBody = (rowData: SplitTableRow) => {
    if (rowData.rowType !== "split" || !canEditSplits) {
      return null;
    }

    const split = rowData as OrderItemPriceSplit & { rowType: "split" };

    return (
      <div className="flex justify-center gap-2">
        <button
          type="button"
          onClick={() => openEditModal(split)}
          className="text-sky-600 hover:text-sky-900"
          title="Edit split"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => askDeleteSplit(split)}
          className="text-red-600 hover:text-red-900"
          title="Delete split"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    );
  };

  if (!splits.length && !(canEditSplits && remainder.quantity > 0)) {
    return null;
  }

  return (
    <div className="mt-4 ml-2">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-gray-700"
          title={isExpanded ? "Collapse price splits" : "Expand price splits"}
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5" />
          )}
          Price splits
          {splitCount > 0 ? (
            <span className="ml-1 font-normal text-gray-500">
              ({splitCount})
            </span>
          ) : null}
        </button>
        {canEditSplits ? (
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-900"
            title="Add price split"
          >
            <PlusIcon className="h-5 w-5" />
            Add split
          </button>
        ) : null}
      </div>
      {isExpanded ? (
        <DataTable
          value={tableRows}
          dataKey="id"
          tableClassName="min-w-full bg-white divide-y divide-gray-300"
          cellClassName={() => "p-2 bg-white border-1-bottom-gray text-sm"}
          emptyMessage="No price splits"
        >
          <Column header="Quantity" body={quantityBody} />
          <Column
            header="Unit Cost"
            body={(row: SplitTableRow) => moneyBody(row.unit_cost)}
            style={{ textAlign: "left" }}
          />
          <Column
            header="Subtotal"
            body={(row: SplitTableRow) => moneyBody(row.subtotal_amount)}
            style={{ textAlign: "right" }}
          />
          <Column
            header="Tax"
            body={(row: SplitTableRow) => moneyBody(row.tax_amount)}
            style={{ textAlign: "right" }}
          />
          <Column
            header="Total"
            body={(row: SplitTableRow) => moneyBody(row.total_cost)}
            style={{ textAlign: "right" }}
          />
          {canEditSplits ? (
            <Column
              header=""
              body={actionsBody}
              style={{ width: "6rem", textAlign: "center" }}
            />
          ) : null}
        </DataTable>
      ) : null}
    </div>
  );
}
